// Building-scope Optimointi view — the loop (Interface A ↑) tier: the shared KB cycle
// indicator (same agent-run ribbon as Konfiguraatio / Datan laatu / Talotekniikan toimivuus /
// Sisäilmaolosuhteet, stories #1612 / #1768 — this page also mounts it), the live-optimization
// toggle, the operational loop around one selected equipment, the recipe in force at the cursor
// (or now), and signal-based verification (operational-loop/verification.ts — ohjaus toteutui /
// rajat pitivät / vaikutus, in the recipe's own units, no euros). No manual "create plan"
// trigger here — that was `/optimize`'s placeholder-recipe path (task #1440), which never
// reached the agent and duplicated Kiinteistötiimi's real `POST /agents/energy-optimizer-v1/run`
// trigger; removed outright rather than kept as a second, non-functional button. The autonomous
// loop stays within the strategy bounds (those live in the Strategia perspective).
import { useEffect, useState } from "react";
import { Beaker, Clock } from "lucide-react";
import type {
  Equipment,
  OperationalLoopReport,
  Recipe,
  RecipeAction,
  RecipeStatus,
  Zone,
} from "@/lib/api/building";
import { servedZoneNames } from "@/lib/building-domain";
import { LiveOptimizationToggle } from "@/components/building/optimization/LiveOptimizationToggle";
import { OperationalLoopSection } from "@/components/building/optimization/operational-loop/OperationalLoopSection";
import {
  MECHANISM_LABELS,
  constraintsAt,
  controlAt,
  impactDeltaAt,
  timeLabelAt,
  windowAt,
  type ConstraintReading,
  type ControlReading,
} from "@/components/building/optimization/operational-loop/verification";
import { EquipmentFilter } from "@/components/building/optimization/EquipmentFilter";
import { MeasuredSignals } from "@/components/building/optimization/MeasuredSignals";
import { VerificationStrip } from "@/components/building/optimization/VerificationStrip";
import {
  loadEnergiatehokkuusOperationalLoop,
  patchEnergiatehokkuusBuildingSnapshot,
  useEnergiatehokkuusBuildingSnapshot,
} from "@/components/building/optimization/useEnergiatehokkuusBuildingSnapshot";
import { FilterChipRow } from "@/components/control-tower/FilterChipRow";
import { SectionPanel } from "@/components/ui/section-panel";
import { TagPillRow } from "@/components/ui/tag-pill";
import { LensHeader } from "@/components/control-tower/perspectives/LensTopicSection";
import { KbCycleRibbon } from "@/components/control-tower/perspectives/KbCycleRibbon";
import { OPPORTUNITY_FINDINGS_FILTER } from "@/components/control-tower/perspectives/lens-topic-metrics";
import {
  cardBase,
  cardSurface,
  cardSubtle,
  cardToneText,
} from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { isOperator } from "@/lib/auth";
import { pickActiveRecipe } from "./active-recipe";

const timeFi = (iso: string) =>
  new Date(iso).toLocaleString("fi-FI", {
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const num = (n: number, decimals = 1) =>
  n.toLocaleString("fi-FI", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const recipeActionDisplay = (a: RecipeAction) => {
  const unit = a.unit ? ` ${a.unit}` : "";
  if (a.baseline === 0) {
    return `${num(a.offset)}${unit}`;
  }
  return `${num(a.offset)}${unit} (lähtö ${num(a.baseline)}${unit})`;
};

const recipeActionResult = (a: RecipeAction) => a.baseline + a.offset;

const STATUS_LABELS: Record<RecipeStatus, string> = {
  pending: "Odottaa",
  simulated: "Simuloitu",
  scheduled: "Ajastettu",
  applied: "Toteutettu",
  superseded: "Korvattu",
};

export function OptimizationBuildingSection({
  buildingId,
}: {
  buildingId: string;
}) {
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(
    null,
  );
  // The shared cursor across the loop's charts: a step index while hovering, null otherwise.
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const operator = isOperator();
  const snapshot = useEnergiatehokkuusBuildingSnapshot(buildingId);

  const bag = snapshot.status === "ok" ? snapshot.bag : null;
  const building = bag?.building ?? null;
  const recipes = bag?.recipes ?? [];
  const equipment = bag?.equipment ?? [];
  const zones = bag?.zones ?? [];
  const equipmentTypeLabel = bag?.equipmentTypeLabels ?? {};
  const report = bag?.operationalLoopReport ?? null;
  const opportunityFindings = bag?.opportunityFindings ?? null;

  // "Optimization capable" = optimization_enabled, the real per-equipment opt-in — not a type
  // guess. EquipmentFilter only ever offers this set; a capable-but-not-yet-opted-in item (or a
  // sensor/meter, which is never capable) simply never appears here, same honesty rule as the
  // per-equipment card below.
  const optimizableEquipment = equipment.filter((e) => e.optimization_enabled);
  // Optimointi is device-driven (docs/spec/concepts.md → "Recipe source and target":
  // Recipe.equipment_id is singular) — exactly one device is always in scope, defaulting to the
  // first optimization-capable item until the operator picks another via EquipmentFilter. Zone
  // is a data source that device's Tilojen tarve panel reads (the backend's operational-loop
  // endpoint resolves which zones it serves), never a second, independently-narrowing filter
  // dimension.
  const selectedEquipment =
    optimizableEquipment.find((e) => e.id === selectedEquipmentId) ??
    optimizableEquipment[0] ??
    null;
  const selectedEquipmentIdResolved = selectedEquipment?.id ?? null;

  useEffect(() => {
    setSelectedEquipmentId(null);
    setHoverIndex(null);
  }, [buildingId]);

  useEffect(() => {
    setHoverIndex(null);
    if (!selectedEquipmentIdResolved) return;
    void loadEnergiatehokkuusOperationalLoop(
      buildingId,
      selectedEquipmentIdResolved,
    );
  }, [buildingId, selectedEquipmentIdResolved]);

  if (snapshot.status === "loading" || !bag || !building) {
    return (
      <div
        className={cn(
          cardBase,
          "p-6 text-sm text-muted-foreground animate-pulse",
        )}
      >
        Ladataan optimointisilmukkaa…
      </div>
    );
  }

  const scopedRecipes = selectedEquipment
    ? recipes.filter((r) => r.equipment_id === selectedEquipment.id)
    : [];

  return (
    <div className="space-y-6">
      <LensHeader
        eyebrow="Energiatehokkuus · näkymä"
        title={`${building.name} hengittää`}
        description="Ennuste → Optimointi → Simulointi → Ohjaus → Verifiointi · 15 min sykli · toimii strategian rajoissa"
      />
      <KbCycleRibbon
        buildingId={buildingId}
        lens={{
          filter: OPPORTUNITY_FINDINGS_FILTER,
          findings: opportunityFindings,
        }}
        cycle={{
          evaluationRun: bag.kbEvaluationRun,
          ticks: bag.kbTickSummaries,
        }}
      />
      <LiveOptimizationToggle
        building={building}
        operator={operator}
        onUpdated={(updated) =>
          patchEnergiatehokkuusBuildingSnapshot(buildingId, updated)
        }
      />
      {optimizableEquipment.length > 1 && (
        <FilterChipRow label="Talotekniikka">
          <EquipmentFilter
            equipment={optimizableEquipment}
            active={selectedEquipment?.id ?? ""}
            onChange={setSelectedEquipmentId}
          />
        </FilterChipRow>
      )}
      {selectedEquipment && (
        <OperationalLoopSection
          equipment={selectedEquipment}
          report={report}
          onHover={setHoverIndex}
        />
      )}
      <ActiveRecipeSection
        equipment={selectedEquipment}
        recipes={scopedRecipes}
        report={report}
        hoverIndex={hoverIndex}
        zones={zones}
        typeLabel={equipmentTypeLabel}
      />
      <VerificationStrip report={report} />
    </div>
  );
}

// ─── Aktiivinen resepti ───────────────────────────────────────────────────────
//
// The recipe in force at one instant on the loop's timeline — the cursor's step while any loop
// chart is hovered, "now" otherwise. What matters is the commanded value on that 15-min step
// (the same series Optimointiresepti plots), plus whether it happened and whether bounds held.
// A Recipe document is only created when a mechanism fires (and after cooldown), so most ticks
// have a command without a new Recipe row — reasoning/status come from the last Recipe that
// set the policy, when one exists. Empty only when that bucket has no command.
function ActiveRecipeSection({
  equipment,
  recipes,
  report,
  hoverIndex,
  zones,
  typeLabel,
}: {
  equipment: Equipment | null;
  recipes: Recipe[];
  report: OperationalLoopReport | null;
  hoverIndex: number | null;
  zones: Zone[];
  typeLabel: Record<string, string>;
}) {
  const index = report ? (hoverIndex ?? report.window.now_index) : null;
  const window = report && index != null ? windowAt(report, index) : null;
  const control =
    report && index != null ? controlAt(report, window, index) : [];
  const hasCommand = control.some((c) => c.commanded != null);
  const recipe = report
    ? (recipes.find((r) => r.id === window?.recipe_id) ?? null)
    : pickActiveRecipe(recipes);
  const cursor = report && index != null && hoverIndex != null;
  const isFuture =
    report != null && index != null && index > report.window.now_index;

  return (
    <SectionPanel
      icon={Beaker}
      // Distinct from RecipeChart's "Optimointiresepti · {laite}" chart title (operational-loop/
      // RecipeChart.tsx) — that panel plots a rolling -24h..+24h curve of resepti actions, this
      // one shows the single 15 min sykli (optimizer tick cadence, ~15-20 min,
      // services/agent/.../optimizer.py) in force at the cursor or now.
      title="Aktiivinen resepti · 15 min sykli"
      subtitle={
        report && index != null
          ? cursor
            ? `Kursori · klo ${timeLabelAt(report, index)} — liikuta kursoria kaavioissa`
            : `Nyt · klo ${timeLabelAt(report, index)} — liikuta kursoria kaavioissa nähdäksesi aiemman syklin`
          : "1 laite silmukassa"
      }
    >
      {!equipment ? (
        <p className="text-sm text-muted-foreground">
          Ei laitteita valitulla rajauksella.
        </p>
      ) : (
        <div className={cn(cardSubtle, "p-3 space-y-3")}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">
                {equipment.name}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {typeLabel[equipment.equipment_type] ??
                  equipment.equipment_type}
                {window?.mechanism
                  ? ` · ${MECHANISM_LABELS[window.mechanism]}`
                  : ""}
              </div>
            </div>
            <TagPillRow names={servedZoneNames(equipment, zones)} />
          </div>

          {!hasCommand && !recipe ? (
            <p className="text-xs text-muted-foreground">
              {report && index != null
                ? "Ei ohjausta tällä 15 min askeleella."
                : "Ei aktiivista reseptiä. Silmukka luo reseptin, kun se havaitsee optimointimahdollisuuden."}
            </p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <InfoBlock
                  label="Perustelu"
                  value={recipe?.reasoning_summary ?? "—"}
                />
                <InfoBlock
                  label="Tila"
                  value={
                    recipe
                      ? `${STATUS_LABELS[recipe.status]}${recipe.applied_at ? ` · ${timeFi(recipe.applied_at)}` : ""}`
                      : "Ohjaus voimassa"
                  }
                />
                <InfoBlock
                  label="Kirjoitettu ohjaus"
                  value={
                    control
                      .filter((c) => c.commanded != null)
                      .map(
                        (c) =>
                          `${num(c.commanded as number)}${c.unit ? ` ${c.unit}` : ""}`,
                      )
                      .join(" · ") ||
                    (recipe
                      ? recipe.actions
                          .map((a) => recipeActionDisplay(a))
                          .join(" · ")
                      : "—")
                  }
                  tone="success"
                />
                <InfoBlock
                  label="Varmuus"
                  value={
                    recipe?.simulated_impact
                      ? `${Math.round(recipe.simulated_impact.confidence * 100)} % · BuildingTarget-rajoissa`
                      : "—"
                  }
                />
              </div>
              {report && index != null && (
                <InstantVerification
                  control={control}
                  constraints={constraintsAt(report, index)}
                  effectPct={impactDeltaAt(report, index)}
                  isFuture={isFuture}
                />
              )}
              {recipe?.measured_response && (
                <MeasuredSignals measured={recipe.measured_response} />
              )}
              {recipe && <ScheduledSetpoints recipe={recipe} />}
            </div>
          )}
        </div>
      )}
    </SectionPanel>
  );
}

// The three questions at one instant (verification.ts): a check that could not be evaluated is
// shown as a gap ("—"), never as a pass or a fail.
function InstantVerification({
  control,
  constraints,
  effectPct,
  isFuture,
}: {
  control: ControlReading[];
  constraints: ConstraintReading[];
  effectPct: number | null;
  isFuture: boolean;
}) {
  const value = (n: number | null, unit: string | null) =>
    n == null ? "—" : `${num(n)}${unit ? ` ${unit}` : ""}`;

  if (isFuture) {
    return (
      <p className="text-[11px] text-muted-foreground">
        Ennuste — kirjoitettu ohjaus on suunniteltu, mitattavaa ei vielä ole.
      </p>
    );
  }
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        Tässä syklissä
      </div>
      {control.map((c) => (
        <CheckLine
          key={c.parameter}
          label="Ohjaus"
          verdict={c.followed}
          text={`${c.label}: ${value(c.commanded, c.unit)} kirjoitettu · ${value(c.measured, c.unit)} mitattu · perusura ${value(c.baseline, c.unit)}${c.measured == null || c.baseline == null ? " — ei mittausparia" : ""}`}
        />
      ))}
      {constraints.length === 0 ? (
        <CheckLine
          label="Rajat"
          verdict={null}
          text="strategiassa ei asetettuja rajoja"
        />
      ) : (
        constraints.map((c) => (
          <CheckLine
            key={`${c.signal}-${c.comparator}-${c.threshold}`}
            label="Rajat"
            verdict={c.ok}
            text={`${c.label} ${value(c.worst, c.unit)}${c.zoneName ? ` (${c.zoneName})` : ""} · raja ${c.comparator === "<=" ? "enint." : "väh."} ${value(c.threshold, c.unit)}`}
          />
        ))
      )}
      <CheckLine
        label="Vaikutus"
        verdict={effectPct == null ? null : effectPct <= 0 ? true : false}
        text={
          effectPct == null
            ? "puhallinsähkö: ei laskettavissa tässä syklissä"
            : `puhallinsähkö ${effectPct > 0 ? "+" : ""}${num(effectPct)} %-yks. ajastetusta tehosta vs. perusura`
        }
      />
    </div>
  );
}

function CheckLine({
  label,
  verdict,
  text,
}: {
  label: string;
  verdict: boolean | null;
  text: string;
}) {
  const mark =
    verdict == null
      ? { glyph: "—", className: cardToneText.empty }
      : verdict
        ? { glyph: "✓", className: cardToneText.ok }
        : { glyph: "✗", className: "text-destructive" };
  return (
    <div className="grid grid-cols-[52px_14px_1fr] gap-2 items-baseline text-[11px]">
      <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </span>
      <span className={cn("font-mono font-semibold", mark.className)}>
        {mark.glyph}
      </span>
      <span className="font-mono text-foreground/90 min-w-0 break-words">
        {text}
      </span>
    </div>
  );
}

function ScheduledSetpoints({ recipe }: { recipe: Recipe }) {
  const now = Date.now();
  const upcoming = recipe.actions
    .filter((a) => a.scheduled_at && new Date(a.scheduled_at).getTime() > now)
    .sort((a, b) => (a.scheduled_at! < b.scheduled_at! ? -1 : 1))
    .slice(0, 24);

  if (upcoming.length === 0) return null;

  const values = upcoming.map((a) => recipeActionResult(a));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const heightPct = (v: number) =>
    range > 0 ? 20 + ((v - min) / range) * 80 : 60;

  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
        Seuraavat 6 h · suunnitellut setpointit
      </div>
      <div className="flex gap-1 items-end">
        {upcoming.map((a, i) => (
          <div
            key={i}
            title={`${timeFi(a.scheduled_at!)} · ${a.parameter} · ${recipeActionDisplay(a)}`}
            className="flex-1 h-10 rounded-sm border border-border/60 bg-primary/10 relative overflow-hidden"
          >
            <div
              className="absolute bottom-0 left-0 right-0 bg-primary/60"
              style={{ height: `${heightPct(recipeActionResult(a))}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1.5 space-y-1">
        {upcoming.slice(0, 6).map((a, i) => (
          <div
            key={i}
            className={cn(
              cardSubtle,
              "flex items-center gap-3 px-2.5 py-1.5 text-xs",
            )}
          >
            <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground shrink-0">
              {timeFi(a.scheduled_at!)}
            </span>
            <span className="flex-1 truncate">{a.parameter}</span>
            <span className="font-medium">{recipeActionDisplay(a)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Osakomponentit + helpers ─────────────────────────────────────────────────

function InfoBlock({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "success";
}) {
  const valueColor = tone === "success" ? "text-success" : "text-foreground";
  return (
    <div className={cn(cardSurface, "p-3")}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div className={cn("mt-1 text-sm font-medium leading-snug", valueColor)}>
        {value}
      </div>
    </div>
  );
}
